import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArchiveIcon from '@mui/icons-material/Archive';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import PersonIcon from '@mui/icons-material/Person';
import RestoreIcon from '@mui/icons-material/Restore';
import SchoolIcon from '@mui/icons-material/School';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { fetchUsers } from '../../api/admin';
import { fetchAllStudents, createStudent, updateStudent, deleteStudent, archiveStudent, restoreStudent, fetchStudentSummary, toggleStudentStar } from '../../api/students';
import StudentFormDialog, { SUBJECT_OPTIONS, GRADE_OPTIONS } from '../../components/forms/StudentFormDialog';
import { ROLES } from '../../utils/roles';

const B = { primary: '#4357AD', dark: '#1E2A4A' };
const cardSx = { borderRadius: 4, boxShadow: '0 2px 12px rgba(67,87,173,0.08)', bgcolor: '#fff' };
const avatarColors = ['#4357AD', '#22C55E', '#F59E0B', '#EC4899', '#8B5CF6', '#06B6D4', '#EF4444', '#14B8A6'];

const statusConfig = {
  debt: { bg: '#FEF2F2', border: '#FECACA', color: '#EF4444', label: 'Борг' },
  low: { bg: '#FFFBEB', border: '#FDE68A', color: '#F59E0B', label: '1 заняття' },
  ok: { bg: '#F0FDF4', border: '#BBF7D0', color: '#22C55E', label: 'Оплачено' },
  unknown: { bg: '#fff', border: '#EDE9FE', color: '#8891A5', label: '' },
};

const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: 3, '& fieldset': { borderColor: '#EDE9FE' }, '&.Mui-focused fieldset': { borderColor: B.primary } },
};

const StudentsPage = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [search, setSearch] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [tutorFilter, setTutorFilter] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const usersQuery = useQuery({ queryKey: ['users'], queryFn: fetchUsers });
  const studentsQuery = useQuery({ queryKey: ['students', showArchived], queryFn: () => fetchAllStudents(showArchived) });
  const archivedCountQuery = useQuery({ queryKey: ['students-archived-count'], queryFn: () => fetchAllStudents(true), select: (d) => d.length });

  const summaryQueries = useQueries({
    queries: (studentsQuery.data || []).map((student) => ({
      queryKey: ['student-summary', student.id],
      queryFn: () => fetchStudentSummary(student.id),
      enabled: Boolean(student.id),
    })),
  });

  const summaryMap = useMemo(() => {
    const map = {};
    (studentsQuery.data || []).forEach((student, index) => {
      const summary = summaryQueries[index]?.data;
      if (summary) map[student.id] = summary;
    });
    return map;
  }, [studentsQuery.data, summaryQueries]);

  const getPaymentStatus = useCallback((studentId) => {
    const s = summaryMap[studentId];
    if (!s) return 'unknown';
    if (s.debtLessons > 0 || s.remainingCredits === 0) return 'debt';
    if (s.remainingCredits === 1) return 'low';
    return 'ok';
  }, [summaryMap]);

  const gradeFilterOptions = useMemo(() => {
    const base = [{ value: '', label: 'Усі класи' }, ...GRADE_OPTIONS];
    const existing = new Set(base.map((o) => o.value));
    (studentsQuery.data || []).forEach((s) => { if (s.grade && !existing.has(s.grade)) { base.push({ value: s.grade, label: s.grade }); existing.add(s.grade); } });
    return base;
  }, [studentsQuery.data]);

  const subjectFilterOptions = useMemo(() => {
    const base = [{ value: '', label: 'Усі предмети' }, ...SUBJECT_OPTIONS];
    const existing = new Set(base.map((o) => o.value));
    (studentsQuery.data || []).forEach((s) => { if (s.subjects && !existing.has(s.subjects)) { base.push({ value: s.subjects, label: s.subjects }); existing.add(s.subjects); } });
    return base;
  }, [studentsQuery.data]);

  const tutors = useMemo(() => (usersQuery.data || []).filter((u) => u.role === ROLES.TUTOR), [usersQuery.data]);
  const parents = useMemo(() => (usersQuery.data || []).filter((u) => u.role === ROLES.PARENT), [usersQuery.data]);

  const students = useMemo(() => {
    let list = studentsQuery.data || [];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((s) => s.name?.toLowerCase().includes(q));
    }
    if (gradeFilter) list = list.filter((s) => s.grade === gradeFilter);
    if (tutorFilter) list = list.filter((s) => String(s.tutorId) === String(tutorFilter));
    if (subjectFilter) list = list.filter((s) => s.subjects === subjectFilter);
    return [...list].sort((a, b) => {
      const sa = summaryMap[a.id];
      const sb = summaryMap[b.id];
      // Debt first (descending by debtLessons), then low credits, then ok (ascending by remainingCredits)
      const debtA = sa?.debtLessons || 0;
      const debtB = sb?.debtLessons || 0;
      if (debtA !== debtB) return debtB - debtA;
      const credA = sa?.remainingCredits ?? 999;
      const credB = sb?.remainingCredits ?? 999;
      return credA - credB;
    });
  }, [studentsQuery.data, search, gradeFilter, tutorFilter, subjectFilter, getPaymentStatus]);

  const createMutation = useMutation({ mutationFn: createStudent, onSuccess: () => { enqueueSnackbar('Учня додано', { variant: 'success' }); queryClient.invalidateQueries({ queryKey: ['students'] }); }, onError: () => enqueueSnackbar('Не вдалося додати учня', { variant: 'error' }) });
  const updateMutation = useMutation({ mutationFn: ({ id, payload }) => updateStudent(id, payload), onSuccess: () => { enqueueSnackbar('Дані оновлено', { variant: 'success' }); queryClient.invalidateQueries({ queryKey: ['students'] }); queryClient.invalidateQueries({ queryKey: ['student'] }); }, onError: () => enqueueSnackbar('Не вдалося оновити', { variant: 'error' }) });
  const deleteMutation = useMutation({ mutationFn: (id) => deleteStudent(id), onSuccess: () => { enqueueSnackbar('Учня видалено', { variant: 'success' }); queryClient.invalidateQueries({ queryKey: ['students'] }); queryClient.invalidateQueries({ queryKey: ['students-archived-count'] }); }, onError: () => enqueueSnackbar('Не вдалося видалити', { variant: 'error' }) });
  const archiveMutation = useMutation({ mutationFn: (id) => archiveStudent(id), onSuccess: () => { enqueueSnackbar('Учня переміщено в архів', { variant: 'success' }); queryClient.invalidateQueries({ queryKey: ['students'] }); queryClient.invalidateQueries({ queryKey: ['students-archived-count'] }); queryClient.invalidateQueries({ queryKey: ['student'] }); }, onError: () => enqueueSnackbar('Не вдалося архівувати', { variant: 'error' }) });
  const restoreMutation = useMutation({ mutationFn: (id) => restoreStudent(id), onSuccess: () => { enqueueSnackbar('Учня відновлено', { variant: 'success' }); queryClient.invalidateQueries({ queryKey: ['students'] }); queryClient.invalidateQueries({ queryKey: ['students-archived-count'] }); queryClient.invalidateQueries({ queryKey: ['student'] }); }, onError: () => enqueueSnackbar('Не вдалося відновити', { variant: 'error' }) });

  const starMutation = useMutation({
    mutationFn: (id) => toggleStudentStar(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['students'] }),
  });

  const isLoading = usersQuery.isLoading || studentsQuery.isLoading;
  const subjectLabel = (v) => SUBJECT_OPTIONS.find((o) => o.value === v)?.label || v || '—';

  const openDialog = (student) => {
    setDialogOpen(false);
    setSelectedStudent(student || null);
    setTimeout(() => setDialogOpen(true), 0);
  };

  const handleSubmit = async (values) => {
    if (selectedStudent) await updateMutation.mutateAsync({ id: selectedStudent.id, payload: values });
    else await createMutation.mutateAsync(values);
  };

  return (
    <Stack spacing={2.5}>
      {/* Header */}
      <Card sx={{ ...cardSx, background: `linear-gradient(135deg, ${B.dark} 0%, ${B.primary} 100%)` }}>
        <CardContent sx={{ py: 3, px: 3.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={1.5}>
            <Box>
              <Typography variant="h5" fontWeight={800} sx={{ color: '#fff' }}>{showArchived ? 'Архів учнів' : 'Учні'}</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
                {(studentsQuery.data || []).length} {showArchived ? 'в архіві' : 'учнів'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}>
              <Tooltip title={showArchived ? 'Активні учні' : 'Архів'}>
                <IconButton
                  onClick={() => setShowArchived((v) => !v)}
                  sx={{ color: showArchived ? '#FEF3C7' : 'rgba(255,255,255,0.5)', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
                >
                  <Badge badgeContent={!showArchived ? (archivedCountQuery.data || 0) : 0} color="error"
                    sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}>
                    <ArchiveIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
              {!showArchived && (
                <Button
                  variant="contained" size="small" startIcon={<AddIcon />}
                  onClick={() => openDialog(null)}
                  sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }, borderRadius: 3, textTransform: 'none', fontWeight: 700, px: 2.5 }}
                >
                  Додати учня
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Filters */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
        <TextField
          size="small" placeholder="Пошук за іменем..." value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#8891A5' }} /></InputAdornment> }}
          sx={{ ...fieldSx, flex: 1 }}
        />
        <TextField size="small" select label="Клас" value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)} sx={{ ...fieldSx, minWidth: 130 }}>
          {gradeFilterOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <TextField size="small" select label="Предмет" value={subjectFilter} onChange={(e) => setSubjectFilter(e.target.value)} sx={{ ...fieldSx, minWidth: 150 }}>
          {subjectFilterOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <TextField size="small" select label="Репетитор" value={tutorFilter} onChange={(e) => setTutorFilter(e.target.value)} sx={{ ...fieldSx, minWidth: 160 }}>
          <MenuItem value="">Усі репетитори</MenuItem>
          {tutors.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
        </TextField>
      </Stack>

      {/* List */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: B.primary }} /></Box>
      ) : students.length === 0 ? (
        <Card sx={cardSx}>
          <CardContent sx={{ py: 5, textAlign: 'center' }}>
            <SchoolIcon sx={{ fontSize: 48, color: '#C4B5FD', mb: 1 }} />
            <Typography variant="h6" sx={{ color: B.dark }}>{search || gradeFilter || tutorFilter || subjectFilter ? 'Нічого не знайдено' : 'Учнів поки немає'}</Typography>
          </CardContent>
        </Card>
      ) : (
        <Card sx={cardSx}>
          <Stack divider={<Box sx={{ borderBottom: '1px solid #F1F5F9' }} />}>
            {students.map((student, i) => {
              const color = avatarColors[i % avatarColors.length];
              const status = getPaymentStatus(student.id);
              const sc = statusConfig[status];
              const summary = summaryMap[student.id];
              return (
                <Box
                  key={student.id}
                  onClick={() => navigate(`/admin/students/${student.id}`)}
                  sx={{
                    px: 2.5, py: 1.5,
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    cursor: 'pointer',
                    borderLeft: `4px solid ${sc.border}`,
                    bgcolor: sc.bg,
                    transition: 'all 0.15s',
                    '&:hover': { bgcolor: status === 'ok' ? '#E6FAF0' : status === 'debt' ? '#FEE2E2' : status === 'low' ? '#FEF3C7' : '#FAFAFE' },
                  }}
                >
                  {/* Star */}
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); starMutation.mutate(student.id); }}
                    sx={{ flexShrink: 0, color: student.starred ? '#F59E0B' : '#D1D5DB', p: 0.25, '&:hover': { color: '#F59E0B' } }}
                  >
                    {student.starred ? <StarIcon sx={{ fontSize: 20 }} /> : <StarBorderIcon sx={{ fontSize: 20 }} />}
                  </IconButton>

                  {/* Avatar */}
                  <Avatar sx={{ width: 36, height: 36, bgcolor: `${color}15`, color, fontWeight: 700, fontSize: 14, border: `2px solid ${color}30`, flexShrink: 0 }}>
                    {student.name?.charAt(0)?.toUpperCase()}
                  </Avatar>

                  {/* Name */}
                  <Box sx={{ flex: '0 0 160px', minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ color: B.dark }} noWrap>{student.name}</Typography>
                    <Typography variant="caption" sx={{ color: '#8891A5' }}>{student.grade}</Typography>
                  </Box>

                  {/* Subject */}
                  <Chip label={subjectLabel(student.subjects)} size="small" sx={{ bgcolor: '#EDE9FE', color: B.primary, fontWeight: 600, fontSize: 11, height: 22, display: { xs: 'none', sm: 'flex' } }} />

                  {/* Tutor */}
                  <Box sx={{ flex: '0 0 140px', display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
                    <PersonIcon sx={{ fontSize: 14, color: '#8891A5' }} />
                    <Typography variant="caption" sx={{ color: B.dark }} noWrap>{student.tutorName || '—'}</Typography>
                  </Box>

                  {/* Parent */}
                  <Box sx={{ flex: '0 0 140px', display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 0.5 }}>
                    <FamilyRestroomIcon sx={{ fontSize: 14, color: '#8891A5' }} />
                    <Typography variant="caption" sx={{ color: B.dark }} noWrap>{student.parentName || '—'}</Typography>
                  </Box>

                  {/* Payment status */}
                  <Box sx={{ flex: 1 }} />
                  {status !== 'unknown' && (
                    <Chip
                      icon={status === 'debt' ? <WarningAmberIcon sx={{ fontSize: 14 }} /> : undefined}
                      label={
                        status === 'debt' && summary?.debtLessons > 0
                          ? `Борг (${summary.debtLessons})`
                          : status === 'debt'
                            ? 'Борг (0)'
                            : status === 'low'
                              ? '1 заняття'
                              : `Оплачено (+${summary?.remainingCredits || 0})`
                      }
                      size="small"
                      sx={{
                        bgcolor: sc.bg,
                        color: sc.color,
                        border: `1px solid ${sc.border}`,
                        fontWeight: 700,
                        fontSize: 11,
                        height: 24,
                        '& .MuiChip-icon': { color: sc.color },
                      }}
                    />
                  )}

                  {/* Actions */}
                  <Stack direction="row" spacing={0.5} flexShrink={0} onClick={(e) => e.stopPropagation()}>
                    {showArchived ? (
                      <>
                        <Tooltip title="Відновити">
                          <IconButton size="small" onClick={() => restoreMutation.mutate(student.id)} sx={{ color: '#22C55E', '&:hover': { bgcolor: '#D1FAE5' } }}>
                            <RestoreIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Видалити остаточно">
                          <IconButton size="small" onClick={() => setConfirmState({ ...student, forceDelete: true })} sx={{ color: '#EF4444', '&:hover': { bgcolor: '#FEE2E2' } }}>
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Tooltip title="Редагувати">
                          <IconButton size="small" onClick={() => openDialog(student)} sx={{ color: B.primary, '&:hover': { bgcolor: '#F0EDFF' } }}>
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Видалити">
                          <IconButton size="small" onClick={() => setConfirmState(student)} sx={{ color: '#EF4444', '&:hover': { bgcolor: '#FEE2E2' } }}>
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Card>
      )}

      <StudentFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
        tutors={tutors}
        parents={parents}
        initialValues={{
          id: selectedStudent?.id,
          name: selectedStudent?.name || '',
          grade: selectedStudent?.grade || '',
          subjects: selectedStudent?.subjects || '',
          parentContact: selectedStudent?.parentContact || '',
          tutorId: selectedStudent?.tutorId || tutors[0]?.id || '',
          parentId: selectedStudent?.parentId || parents[0]?.id || '',
        }}
        title={selectedStudent ? 'Редагувати учня' : 'Новий учень'}
      />

      {/* Delete / Archive dialog */}
      <Dialog
        open={Boolean(confirmState)}
        onClose={() => setConfirmState(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, border: `2px solid ${B.primary}`, boxShadow: '0 20px 60px rgba(67,87,173,0.18)', overflow: 'hidden' } }}
      >
        <Box sx={{ background: `linear-gradient(135deg, ${B.dark} 0%, ${B.primary} 100%)`, px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <WarningAmberRoundedIcon sx={{ color: '#FEF3C7', fontSize: 22 }} />
          </Box>
          <DialogTitle sx={{ p: 0, color: '#fff', fontWeight: 700, fontSize: '1.15rem', lineHeight: 1.3 }}>
            {confirmState?.forceDelete ? 'Видалити остаточно?' : `Що зробити з ${confirmState?.name}?`}
          </DialogTitle>
        </Box>
        <DialogContent sx={{ px: 3, pt: 3, pb: 1.5 }}>
          <DialogContentText sx={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.6 }}>
            {confirmState?.forceDelete
              ? `Учня "${confirmState?.name}" буде видалено остаточно разом з усіма заняттями, оплатами та оцінками. Цю дію не можна скасувати.`
              : 'Ви можете перемістити учня в архів або видалити остаточно разом з усіма даними.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1, flexDirection: 'column', alignItems: 'stretch' }}>
          {confirmState?.forceDelete ? (
            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
              <Button onClick={() => setConfirmState(null)} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 600, color: '#64748B', px: 3, '&:hover': { bgcolor: '#F1F5F9' } }}>
                Скасувати
              </Button>
              <Button variant="contained" onClick={async () => { await deleteMutation.mutateAsync(confirmState.id); setConfirmState(null); }}
                sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700, px: 3, bgcolor: '#EF4444', '&:hover': { bgcolor: '#DC2626' } }}>
                Видалити
              </Button>
            </Stack>
          ) : (
            <>
              <Button variant="contained" startIcon={<ArchiveIcon />}
                onClick={async () => { await archiveMutation.mutateAsync(confirmState.id); setConfirmState(null); }}
                sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700, bgcolor: '#F59E0B', '&:hover': { bgcolor: '#D97706' }, justifyContent: 'flex-start', px: 2.5 }}>
                В архів (можна відновити)
              </Button>
              <Button variant="contained" startIcon={<DeleteOutlineIcon />}
                onClick={async () => { await deleteMutation.mutateAsync(confirmState.id); setConfirmState(null); }}
                sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700, bgcolor: '#EF4444', '&:hover': { bgcolor: '#DC2626' }, justifyContent: 'flex-start', px: 2.5 }}>
                Видалити остаточно
              </Button>
              <Button onClick={() => setConfirmState(null)} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 600, color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
                Скасувати
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default StudentsPage;
